import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Grow from '@mui/material/Grow';
import React, { useRef } from 'react';
import { useAppDispatch } from '../../../../../redux/hooks';
import { updateList } from '../../../../../redux/trip/trip.slice';

import './RenameListInput.scss';

type RenameListInputProps = {
  tripId: string;
  list: { id: string; name: string };
  canEdit: boolean;
  isEditingName: boolean;
  setIsEditingName: (newValue: boolean) => void;
  isMobile?: boolean;
  // When provided, called instead of dispatching updateList — allows reuse for divider rename.
  onSave?: (name: string) => void;
};

function RenameListInput({
  tripId,
  list,
  canEdit,
  isEditingName,
  setIsEditingName,
  isMobile = false,
  onSave,
}: RenameListInputProps) {
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);

  const onUpdateListName = (input: HTMLInputElement) => {
    const newListName = input.value;
    if (newListName.length !== 0 && newListName !== list.name) {
      if (onSave) {
        onSave(newListName);
      } else {
        dispatch(updateList({ tripId, listId: list.id, field: 'name', value: newListName }));
      }
    }
    setIsEditingName(false);
  };

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const maxCharacterCount = 100;
    const listName = event.target as HTMLInputElement;
    const text = listName.value;

    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent creating a new line
      onUpdateListName(listName);
    }

    if (
      text.length >= maxCharacterCount &&
      event.key !== 'Backspace' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight'
    ) {
      event.preventDefault(); // Prevent further input
    }
  };

  const cancelRenameList = () => {
    setIsEditingName(false);
  };

  const onSaveButtonClick = () => {
    if (inputRef.current) {
      onUpdateListName(inputRef.current);
    }
  };

  return (
    <div className='rename-list-div'>
      <Grow
        in={isEditingName}
        mountOnEnter
        unmountOnExit
        timeout={{
          enter: 800,
          exit: 400,
        }}
      >
        <div>
          <input
            className='rename-styled-input'
            ref={inputRef}
            defaultValue={list.name}
            onKeyDown={handleOnKeyDown}
            onClick={(e) => e.stopPropagation()}
            autoFocus
            readOnly={!canEdit}
          />
          <div className='rename-list-btns'>
            <div
              className='cancel-rename-list-div'
              onClick={cancelRenameList}
            >
              <CloseRoundedIcon className='cancel-rename-list-btn' />
              <span className='cancel-span'>CANCEL</span>
            </div>
            <div
              className='save-rename-list-div'
              onClick={onSaveButtonClick}
            >
              <CheckRoundedIcon className='save-rename-list-btn' />
              <span className='rename-span'>SAVE</span>
            </div>
          </div>
        </div>
      </Grow>
    </div>
  );
}

RenameListInput.displayName = 'RenameListInput';
export default RenameListInput;
